import pluralize from "pluralize";
import "../../../lib/is_integer_polyfill";

/**
 * Function is used to determine whether an object is one which
 * conforms to the shape of a select object used for the react-select
 * component
 * @param {object} object - The object for which you wish to determine is a select object
 */
const isSelectObject = object =>
  object != null &&
  typeof object === "object" &&
  "value" in object &&
  "label" in object;

/**
 * Transforms a select object, one used for the react-select component,
 * into one that can be mapped to be understood by the rails grape api.
 *
 * eg. mapSelectObject({value:'123', label:'Engineering'}, 'tag') => { key:'tag', value:123 }
 *
 * @param {object} object - the select object for react-select
 * @param {string} key - the key to which the items should be mapped.
 * This value corresponds to the property grape api will understand.
 */
const mapSelectObject = (object, key) => {
  const val = parseInt(object.value, 10) || object.value;
  return {
    key: Number.isInteger(val) ? `${key}_id` : key,
    value: val
  };
};

/**
 * Maps an array of select options for react-select into an array of
 * values that can be understood by the rails grape api.
 * @param {Array} collection - the collection of items to map
 */
const mapSelectCollection = collection =>
  collection.map(o => mapSelectObject(o).value);

/**
 * Transforms a values hash (perhaps used as application state and by Formik) into
 * a hash which can be used by rails grape api.
 * @param {object} values - the hash of values to map.
 * @param {array} blacklist - skip transforming these keys
 */
const GrapeFormat = (values, blacklist = []) => {
  const result = Object.keys(values)
    .map(k => {
      const mapped = {};
      const value = values[k];
      if (blacklist.includes(k)) {
        mapped[k] = value;
        return mapped;
      }
      if (Array.isArray(value)) {
        if (RegExp("Ids$").test(k)) {
          mapped[k] = value;
        } else {
          mapped[`${pluralize.singular(k)}_ids`] = mapSelectCollection(value);
        }
      } else if (isSelectObject(value)) {
        const mappedSelectObject = mapSelectObject(value, k);
        mapped[mappedSelectObject.key] = mappedSelectObject.value;
      } else {
        mapped[k] = value;
      }
      return mapped;
    })
    .reduce((prev, curr) => ({ ...prev, ...curr }), {});
  return result;
};

/**
 * Formats a simple id into one that partially conforms to the react-select format.
 * This is used as a hack for when you have a list of ids that need to run through
 * GrapeFormat but don't conform to the react-select shape.
 * @param {object} record - the record to map
 */
const formatRawAsOption = record => ({
  value: record
});

/**
 * Formats a list of ids into the react-select shape, expected by GrapeFormat.
 * See formatRawAsOption.
 * @param {Array} records - the records to process
 */
const formatRawAsOptions = records => records.map(r => formatRawAsOption(r));

/**
 * Formats an object posessing name/id into one for use in react-select
 * @param {object} record - the record to process. Must have name/id properties.
 */
const formatOption = record => {
  if (record == null) return null;
  return {
    value: record.id,
    label: record.name
  };
};

/**
 * Formats a list of name/id records into objects suitable for use in react-select components
 * @param {records} records - the array of records to process
 */
const formatOptions = records => {
  if (records == null || !Array.isArray(records)) return [];
  return records.map(r => formatOption(r));
};

export { formatOption, formatOptions, formatRawAsOption, formatRawAsOptions };

export default GrapeFormat;



// WEBPACK FOOTER //
// ./app/javascript/app/utils/formatters/formik-grape-formatter.js